import React, { useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import publicApi from '../services/public.api';
import LocationContext from '../context/LocationContext';
import { useCart } from '../context/CartContext';
import { addToCartAnimated } from '../utils/addToCartAnimated';
import ProductCardQuantity, { getDefaultCardSelection, isProductHasVariants } from './ProductCardQuantity';
import ProductCardPriceQtyRow from './ProductCardPriceQtyRow';
import { buildCartProductFromSelection } from '../utils/cartProductHelpers';

const RecommendedOne = () => {
    const [categories, setCategories] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [loading, setLoading] = useState(true);
    const [addedProductId, setAddedProductId] = useState(null);
    const [selections, setSelections] = useState({});
    const [weightHintId, setWeightHintId] = useState(null);

    // Cart context
    const { addToCart } = useCart();

    // Location-based pricing
    const context = useContext(LocationContext);
    const isCasaRegion = context?.isCasaRegion ?? false;

    // Get price based on location
    const getLocationPrice = (product) => {
        if (!product) return 0;
        const priceCasa = parseFloat(product.price_casa || 0);
        const regularPrice = parseFloat(product.price || 0);
        return (isCasaRegion && priceCasa > 0) ? priceCasa : regularPrice;
    };

    const getSelection = (product) =>
        selections[product.id] || getDefaultCardSelection(isProductHasVariants(product));

    const updateSelection = (product, next) => {
        setSelections((prev) => ({ ...prev, [product.id]: next }));
    };

    const getCardUnitPrice = (product) => {
        let base = getLocationPrice(product);
        if (product.discount > 0) {
            base = base * (1 - product.discount / 100);
        }
        const sel = getSelection(product);
        if (isProductHasVariants(product) && sel.weightValue) {
            base *= sel.weightValue;
        }
        return base;
    };

    const handleAddToCart = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        let basePrice = getLocationPrice(product);
        if (product.discount > 0) {
            basePrice = basePrice * (1 - product.discount / 100);
        }
        const built = buildCartProductFromSelection(product, basePrice, getSelection(product));
        if (built.error) {
            setWeightHintId(product.id);
            setTimeout(() => setWeightHintId(null), 2000);
            return;
        }
        addToCartAnimated({
            event: e,
            product: built.product,
            addToCart,
            quantity: built.quantity,
        });
        setAddedProductId(product.id);
        setTimeout(() => setAddedProductId(null), 1500);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await publicApi.getCategoriesWithProducts();
                if (response.success && response.data) {
                    // Get all categories (including empty ones)
                    setCategories(response.data);
                    
                    // Collect all products for "Tous" tab
                    const all = [];
                    response.data.forEach(cat => {
                        cat.products.forEach(p => {
                            all.push({ ...p, categoryName: cat.name });
                        });
                    });
                    setAllProducts(all);
                }
            } catch (error) {
                console.error('Error fetching categories with products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Get image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return 'assets/images/thumbs/product-img7.png';
        if (imagePath.startsWith('http')) return imagePath;
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const storageBaseUrl = API_BASE_URL.replace('/api', '');
        
        // Check if path already starts with /storage/ to avoid double prefix
        if (imagePath.startsWith('/storage/')) return `${storageBaseUrl}${imagePath}`;
        if (imagePath.startsWith('storage/')) return `${storageBaseUrl}/${imagePath}`;
        return `${storageBaseUrl}/storage/${imagePath}`;
    };

    // Format price
    const formatPrice = (price) => {
        return parseFloat(price || 0).toFixed(2);
    };

    // Get current products based on active tab
    const getCurrentProducts = () => {
        if (activeTab === 'all') {
            return allProducts;
        }
        const category = categories.find(cat => cat.id.toString() === activeTab);
        return category ? category.products : [];
    };

    // Render product card
    const renderProductCard = (product, index) => (
        <div key={`${product.id}-${index}`} className="col-12 col-sm-6 col-md-4 col-lg-3 col-xxl-2">
            <div className="product-card h-100 p-8 border border-gray-100 recommended-product-hover rounded-16 position-relative transition-2">
                {product.has_offer && product.discount > 0 ? (
                    <span 
                        className="product-card__badge px-8 py-4 text-sm"
                        style={{ backgroundColor: '#eab802', color: '#000' }}
                    >
                        -{product.discount}%
                    </span>
                ) : (
                    <span className="product-card__badge recommended-badge-new px-8 py-4 text-sm text-white">
                        Nouveau
                    </span>
                )}
                <Link
                    to={`/product-details/${product.id}`}
                    className="product-card__thumb flex-center"
                >
                    <img 
                        src={getImageUrl(product.image)} 
                        alt={product.name}
                        className="product-card__thumb-img"
                    />
                </Link>
                <div className="product-card__content p-sm-2">
                    <h6 className="title text-lg fw-semibold mt-12 mb-8">
                        <Link to={`/product-details/${product.id}`} className="link text-line-2">
                            {product.name}
                        </Link>
                    </h6>
                    <div className="flex-align gap-4 d-none d-sm-flex">
                        <span className="text-main-600 text-md d-flex">
                            <i className="ph-fill ph-storefront" />
                        </span>
                        <span className="text-gray-500 text-xs">
                            Groupe Délices Benkirane
                        </span>
                    </div>
                    <div className="product-card__content mt-12">
                        <ProductCardPriceQtyRow
                            price={
                                product.has_offer && product.discount > 0 ? (
                                    <>
                                        <span className="text-heading text-md fw-semibold">
                                            {formatPrice(getLocationPrice(product) * (1 - product.discount / 100))} DH{" "}
                                            <span className="text-gray-500 fw-normal">/Qty</span>
                                        </span>
                                        {!isCasaRegion && (
                                            <span className="text-gray-400 text-md fw-semibold text-decoration-line-through d-block mt-4">
                                                {formatPrice(getLocationPrice(product))} DH
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-heading text-md fw-semibold">
                                        {formatPrice(getCardUnitPrice(product))} DH{" "}
                                        <span className="text-gray-500 fw-normal">/Qty</span>
                                    </span>
                                )
                            }
                            hint={weightHintId === product.id ? (
                                <span className="text-danger text-xs fw-medium d-block mt-4">
                                    Choisissez un poids
                                </span>
                            ) : null}
                            quantityControl={
                                <ProductCardQuantity
                                    hasVariants={isProductHasVariants(product)}
                                    selection={getSelection(product)}
                                    onChange={(next) => updateSelection(product, next)}
                                    collapsedUntilInteraction
                                />
                            }
                        />
                        <div className="flex-align gap-6">
                            <span className="text-xs fw-bold text-gray-600">
                                {parseFloat(product.rating_average || 0).toFixed(1)}
                            </span>
                            <span className="text-15 fw-bold text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-xs fw-bold text-gray-600">
                                ({product.rating_count || 0})
                            </span>
                        </div>
                        <button
                            onClick={(e) => handleAddToCart(e, product)}
                            disabled={addedProductId === product.id}
                            className={`product-card__cart btn ${
                                addedProductId === product.id 
                                    ? 'btn-success' 
                                    : 'recommended-btn-black'
                            } text-white hover-text-white py-11 px-24 rounded-pill flex-align gap-8 mt-24 w-100 justify-content-center`}
                            style={{
                                transition: 'all 0.3s ease',
                                border: 'none',
                                cursor: addedProductId === product.id ? 'default' : 'pointer'
                            }}
                        >
                            {addedProductId === product.id ? (
                                <>Ajouté <i className="ph ph-check-circle" /></>
                            ) : (
                                <>Ajouter au Panier <i className="ph ph-shopping-cart" /></>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <section className="recommended pt-80">
                <div className="container container-lg">
                    <div className="section-heading">
                        <h5 className="mb-0">Nos Offres Spéciales</h5>
                    </div>
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (allProducts.length === 0) {
        return null;
    }

    const currentProducts = getCurrentProducts();

    return (
        <section className="recommended pt-80">
            <div className="container container-lg">
                <div className="section-heading flex-between flex-wrap gap-16">
                    <h5 className="mb-0">Nos Offres Spéciales</h5>
                    <div className="nav-wrapper position-relative" style={{ maxWidth: '70%', overflow: 'hidden' }}>
                        <ul 
                            className="nav common-tab nav-pills flex-nowrap" 
                            id="pills-tab" 
                            role="tablist"
                            style={{ 
                                overflowX: 'auto', 
                                scrollBehavior: 'smooth',
                                whiteSpace: 'nowrap',
                                msOverflowStyle: 'none',
                                scrollbarWidth: 'none',
                                WebkitOverflowScrolling: 'touch'
                            }}
                        >
                            <li className="nav-item" role="presentation">
                                <button
                                    className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('all')}
                                    type="button"
                                    role="tab"
                                >
                                    Tous
                                </button>
                            </li>
                            {categories.map((category) => (
                                <li key={category.id} className="nav-item" role="presentation">
                                    <button
                                        className={`nav-link ${activeTab === category.id.toString() ? 'active' : ''}`}
                                        onClick={() => setActiveTab(category.id.toString())}
                                        type="button"
                                        role="tab"
                                        style={{ whiteSpace: 'nowrap' }}
                                    >
                                        {category.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="tab-content" id="pills-tabContent">
                    <div className="tab-pane fade show active" role="tabpanel" tabIndex={0}>
                        <div className="row g-12">
                            {currentProducts.map((product, index) => renderProductCard(product, index))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default RecommendedOne